import { ElementRef, ViewContainerRef } from '@angular/core';
import {
  IContentControlStudioViewModel,
  IGetVideoCallbacksContext,
  IStudioEventsRef,
} from 'app/js/src/domain/content-control/studio';
import { IContentControlVideoSource } from 'app/js/src/domain/content-control/i-content-control-video-source';
import { IContentControlManager } from 'app/js/src/domain/content-control/manager/i-content-control-manager';
import { IEvrideoDialogService } from 'app/js/src/domain/common/i-evrideo-dialog-service';
import { IContentControlStudioTimeline } from './content-control-studio-timeline/i-content-control-studio-timeline';
import { ContentControlVideoSource } from 'app/js/src/domain/content-control/content-control-video-source';
import { HlsStreamVideoSource } from 'app/js/src/domain/content-control/hls-stream-video-source';

import { ContentControlledEditingDirective } from 'app/js/src/domain/content-control/editing-directive';
import {
  ContentControlledEventActionEnum,
  ContentControlledMediaTypeEnum,
} from 'app/js/src/domain/content-control/enums';
import { ContentControlledKeyframe } from 'app/js/src/domain/content-control/keyframe';
import {
  UnixTimestampToFormattedTime,
  UnixTimestampToFormattedTimecode,
} from 'app/js/src/domain/common/ui-utils';
import { ContentControlCanvasService } from 'app/js/src/infrastructure/content-control/content-control-canvas.service';
import { MediaRepository } from 'app/js/src/domain/media-repository';
import { ContentControlledLiveSettings } from 'app/js/src/domain/content-control/settings/ContentControlledLiveSettings';
import { ContentControlStudioVODViewModel } from './content-control-studio-vod-view-model';
import { StudioAudioManager } from './helpers/studio-audio-manager';
import { StudioAudioManagerLive } from './helpers/studio-audio-manager-live';
import { VideoClip } from './content-control-studio-actions/content-control-studio-actions.component';

/**
 * Live ViewModel V2 that extends {@link ContentControlStudioVODViewModel} for live HLS streams.
 *
 * Hierarchy:
 * ```
 * ContentControlStudioBaseViewModel (abstract)
 *   -> ContentControlStudioVODViewModel
 *        -> ContentControlStudioLiveViewModelV2
 * ```
 *
 * Delegates the full HLS.js lifecycle (instance creation, source loading, media
 * attachment, error recovery, manifest parsing, buffer configuration, fragment
 * tracking, and seeking) to {@link HlsStreamVideoSource}. This class focuses on
 * UI state management, video clip overlays, and studio-specific concerns.
 *
 * Video clip overlays are supported: when an active event has a {@link ContentControlledEventActionEnum.VideoClip}
 * action, a secondary `<video>` element is lazily created, synced with the main HLS stream,
 * and returned from `_getVideoSource()` for canvas rendering.
 */
export class ContentControlStudioLiveViewModelV2
  extends ContentControlStudioVODViewModel
{
  // ---------------------------------------------------------------------------
  // Constants
  // ---------------------------------------------------------------------------

  /** Threshold in seconds within which the player is considered to be at the live edge. */
  private readonly LIVE_THRESHOLD_SECS = 1;

  /** Timeout in milliseconds before seek recovery auto-resumes playback. */
  private readonly SEEK_RECOVERY_TIMEOUT_MS = 1000;

  // ---------------------------------------------------------------------------
  // HLS state fields
  // ---------------------------------------------------------------------------

  /** The HLS video source managing the full HLS.js lifecycle. */
  private _hlsVideoSource: HlsStreamVideoSource;

  /** Cached current time for UI display when video is paused (player seconds). */
  private _currentTimeForUISecs: number = 0;

  /** Whether a seek operation is in progress. */
  private _seeking = false;

  /** Timeout handle for seek recovery. */
  private _seekTimeoutInterval: ReturnType<typeof setTimeout>;

  /** Stored event listeners for cleanup on destroy. */
  private _boundEventHandlers: { source: IContentControlVideoSource; event: string; handler: EventListener }[] = [];

  /** Whether the player is at the live edge. */
  private _isLive = true;

  /** Whether console logging is enabled. */
  private _printEnabled: boolean;

  /** Whether {@link setupVideo} has completed, used to gate the video-loaded callback. */
  private _videoSetupComplete = false;

  /** Last duration (seconds) used for zoom level calculation, to avoid redundant recalculations. */
  private _lastZoomDurationSecs = 0;

  // ---------------------------------------------------------------------------
  // Video clip management fields
  // ---------------------------------------------------------------------------

  /** Cached video elements keyed by media ID for video clip overlays. */
  private _videoClipElements = new Map<number, IContentControlVideoSource>();

  /** Currently playing clip video element, or null when showing main stream. */
  private _videoClipVideoElement: IContentControlVideoSource | null = null;

  /** The directive for the currently active video clip overlay. */
  private _activeVideoClipDirective: ContentControlledEditingDirective | null = null;

  /** Cleanup functions for main video event listeners (play/pause/seeked sync). */
  private _mainVideoListenerRemovers: (() => void)[] = [];

  /** Whether main-to-clip video sync listeners have been set up. */
  private _mainVideoSyncSetup = false;

  // ---------------------------------------------------------------------------
  // Constructor
  // ---------------------------------------------------------------------------

  /**
   * @param canvasService                Canvas rendering service
   * @param videoElement                 Raw `<video>` element to attach HLS media to
   * @param oneFrameSecs                 Duration of a single frame (seconds), e.g. `1/25`
   * @param hlsStreamUrl                 HLS manifest URL (.m3u8) to load
   * @param _mediaRepository             Repository for fetching clip preview URLs
   * @param _contentControlItemSettings  Live studio settings (allowed deviation, delay)
   * @param componentContext             Shared context for video callbacks
   * @param debug                        Enable HLS.js debug logging (default: false)
   * @param print                        Enable provider console logging (default: true)
   */
  constructor(
    canvasService: ContentControlCanvasService,
    videoElement: HTMLVideoElement,
    oneFrameSecs: number,
    hlsStreamUrl: string,
    private _mediaRepository: MediaRepository,
    private _contentControlItemSettings: ContentControlledLiveSettings,
    componentContext: IGetVideoCallbacksContext,
    debug?: boolean,
    print?: boolean,
  ) {
    const videoFps = +(1 / oneFrameSecs).toPrecision(12);
    const hlsVideoSource = HlsStreamVideoSource.create({
      videoElement,
      hlsStreamUrl,
      fps: videoFps,
      debug,
    });

    super(canvasService, hlsVideoSource, componentContext);

    this._hlsVideoSource = hlsVideoSource;
    this._printEnabled = print ?? true;

    this._hlsVideoSource.registerLifecycleCallbacks({
      onManifestReady: () => this._onManifestReady(),
      onAudioTracksUpdated: () => this.setupAudio(this.audioOptions),
      onSubtitleTracksUpdated: () => this.setupSubtitles(this.subtitleOptions),
      onLevelUpdated: () => this._onLevelUpdated(),
      onFatalError: (_type, data) => this._log('error', 'Fatal HLS error:', data),
    });
  }

  // ---------------------------------------------------------------------------
  // Public HLS state getters (used by timeline, debug panel, header)
  // ---------------------------------------------------------------------------

  /** The underlying HLS video source managing the full HLS.js lifecycle. */
  public get hlsVideoSource(): HlsStreamVideoSource { return this._hlsVideoSource; }

  /** Whether the player is at the live edge of the HLS stream. */
  public get isLive(): boolean { return this._isLive; }

  /** Live zoom mode state from the zoom manager. */
  public get isLiveZoomMode(): boolean { return this._zoom?.isLiveZoomMode ?? false; }

  /**
   * Current player time for UI display (seconds).
   * Returns cached value when paused to prevent drift; live value when playing.
   */
  public get currentTimeForUISecs(): number {
    return this._hlsVideoSource.paused
      ? this._currentTimeForUISecs
      : this._hlsVideoSource.currentTimeSecs;
  }


  /** User-defined allowed deviation for the studio in ms. */
  public get allowedDeviationMs(): number {
    return this._contentControlItemSettings.studioAllowedDeviationMs;
  }

  /** User-defined delay for the studio in ms. */
  public get setDelayMs(): number {
    return this._contentControlItemSettings.studioDefinedDelayMs;
  }

  /**
   * [Current Delay] Total delay minus sum of Cut segment durations
   * plus sum of Insert segment durations, clamped to >= 0.
   */
  public get videoWithoutCutDirectivesWithInsertsMs(): number {
    const sumOfCutDurationsMs = this.segments
      .filter((s) => s.action === ContentControlledEventActionEnum.Cut)
      .reduce((sum, s) => sum + s.durationMs, 0);
    const sumOfInsertsDurationsMs = this.segments
      .filter((s) => s.action === ContentControlledEventActionEnum.VideoClip)
      .reduce((sum, s) => sum + s.durationMs, 0);
    return Math.max(0, (this.setDelayMs - sumOfCutDurationsMs) + sumOfInsertsDurationsMs);
  }

  /** Over/under-run in ms: allowedDeviation + setDelay - currentDelay. */
  public get overUnderRunMs(): number {
    return Math.max(0, (this.allowedDeviationMs + this.setDelayMs) - this.videoWithoutCutDirectivesWithInsertsMs);
  }

  // ---------------------------------------------------------------------------
  // Live zoom (public)
  // ---------------------------------------------------------------------------

  /** Enters or deepens live zoom mode if playlist is long enough. */
  public timelineZoomLive(): void {
    if ((this._hlsVideoSource.convertPlayerRelativeTimeToUtcTimestampMs(this.currentTimeForUISecs) - 60000) < (this._hlsVideoSource.firstFragment?.programDateTime ?? 0)) {
      return;
    }
    if (this._zoom.isLiveZoomMode) {
      this._timelineRef?.zoomInLive?.();
    } else {
      this._zoom.enterLiveZoomMode();
    }
  }

  /** Exits live zoom mode. */
  public exitLiveZoomMode(): void { this._zoom?.exitLiveZoomMode(); }

  // ---------------------------------------------------------------------------
  // Overrides from VOD (protected/public)
  // ---------------------------------------------------------------------------

  /**
   * Overrides setup to inject the HLS-aware video source (`this._hlsVideoSource`)
   * instead of the plain `ContentControlVideoSource` passed from the component.
   * Without this, the base VM would use raw `video.currentTime` for seeking
   * rather than UTC-based HLS timestamps.
   */
  public override setupViewModel(
    manager: IContentControlManager,
    dialogService: IEvrideoDialogService,
    viewContainerRef: ViewContainerRef,
    timelineRef: IContentControlStudioTimeline,
    eventsRef: IStudioEventsRef,
    playerElement: HTMLDivElement,
    loadingText: HTMLElement,
    _videoSource: IContentControlVideoSource,
    canvasElementRef: ElementRef<HTMLCanvasElement>,
    drawingCanvasElementRef: ElementRef<HTMLCanvasElement>,
    detectChangesFn: () => void,
  ): void {
    super.setupViewModel(
      manager, dialogService, viewContainerRef, timelineRef, eventsRef,
      playerElement, loadingText,
      this._hlsVideoSource,
      canvasElementRef, drawingCanvasElementRef, detectChangesFn,
    );
  }

  /**
   * Returns the clip video element when a VideoClip event is active,
   * otherwise returns the main HLS video element.
   * Live differs from VOD because clip videos are synced to the main stream's
   * UTC-based timestamps.
   */
  protected override _getVideoSource(activeEvents?: ContentControlledEditingDirective[]): IContentControlVideoSource {
    const videoClipEvent = activeEvents?.find(
      (e) => e.action === ContentControlledEventActionEnum.VideoClip && e.mediaId != null,
    );
    const main = this._mainVideoElement;

    if (videoClipEvent && videoClipEvent.mediaId != null && main) {
      this._setupMainVideoSync();
      const clipElement = this._getOrCreateClipVideoElement(videoClipEvent.mediaId);
      this._videoClipVideoElement = clipElement;

      const isNewEntry = this._activeVideoClipDirective !== videoClipEvent;
      this._activeVideoClipDirective = videoClipEvent;

      if (isNewEntry) {
        const mainTimestampMs = this._hlsVideoSource.convertPlayerRelativeTimeToUtcTimestampMs(main.currentTimeSecs);
        const clipTimeSec = (mainTimestampMs - videoClipEvent.startsAtMs) / 1000;
        this._seekClipToOffset(clipElement, clipTimeSec);
      }

      if (main.paused && !clipElement.paused) {
        clipElement.pause();
      } else if (!main.paused && clipElement.paused) {
        clipElement.play();
      }
      return clipElement;
    }

    this._videoClipVideoElement = null;
    this._activeVideoClipDirective = null;
    return main;
  }

  /**
   * Returns a live-specific audio manager that delegates track switching to HLS.js.
   */
  protected override createAudioManager(): StudioAudioManager {
    return new StudioAudioManagerLive(
      () => this._getVideoSource(),
      this._videoContainerElement,
      this._manager,
      this._timelineRef,
      this._playback,
    );
  }

  /**
   * Sets up DOM event handlers for live playback (play, pause, seeking, seeked,
   * timeupdate, contextmenu). HLS-specific event handling (manifest parsing,
   * error recovery, audio/subtitle track updates, fragment tracking) is managed
   * by {@link HlsStreamVideoSource} via lifecycle callbacks.
   */
  public override setupVideo(): void {
    const noop = () => {};

    this._addPlayerListener('play', noop);
    this._addPlayerListener('pause', noop);
    this._addPlayerListener('pause', () => {
      this._currentTimeForUISecs = this._hlsVideoSource.rawCurrentTimeSecs;
    });

    this._addPlayerListener('seeking', () => {
      this._seeking = true;
      this._seekTimeoutInterval = setTimeout(() => {
        if (this._seeking) {
          this._seeking = false;
          this._hlsVideoSource.play().catch((e) => this._log('warn', 'Auto-play failed:', e));
        }
      }, this.SEEK_RECOVERY_TIMEOUT_MS);
    });
    this._addPlayerListener('seeked', () => {
      clearTimeout(this._seekTimeoutInterval);
      this._seeking = false;
      this._currentTimeForUISecs = this._hlsVideoSource.rawCurrentTimeSecs;
    });
    this._addPlayerListener('timeupdate', noop);
    this._addPlayerListener('contextmenu', (event: Event) => event.preventDefault());

    this._videoSetupComplete = true;
    if (this._hlsVideoSource.isManifestParsed) {
      this.onVideoLoadedEventHandler();
    }
  }

  /**
   * Reads subtitle tracks from the HLS manifest instead of loading from files.
   * The `_subtitleOptions` parameter is intentionally unused; track names are
   * sourced entirely from HLS.js subtitle track metadata.
   */
  public override setupSubtitles(_subtitleOptions: string[]): string[] {
    const options = this._hlsVideoSource.subtitleTrackNames;
    options.unshift(IContentControlStudioViewModel.SK);
    return options;
  }

  /**
   * Reads audio tracks from the HLS manifest instead of loading from files.
   * The `_audioOptions` parameter is intentionally unused; track names are
   * sourced entirely from HLS.js audio track metadata.
   */
  public override setupAudio(_audioOptions: string[]): string[] {
    const options = this._hlsVideoSource.audioTrackNames;
    options.unshift(IContentControlStudioViewModel.VK);
    return options;
  }

  /**
   * Formats time as a UTC timestamp (HH:MM:SS) rather than a relative duration.
   * Live uses absolute timestamps because the timeline represents wall-clock time.
   */
  public override timeFormatFunction(milliseconds: number, justMinutesAndSeconds?: boolean): string {
    const videoFps = this._manager.videoFps;
    const displayInFrames = this._manager.displayContentControlStudioTimeInFrames;
    if (displayInFrames && !justMinutesAndSeconds) {
      return UnixTimestampToFormattedTimecode(milliseconds, videoFps);
    }
    return UnixTimestampToFormattedTime(milliseconds, justMinutesAndSeconds);
  }

  /**
   * Live does not support playback speed changes.
   */
  public override supportsPlaybackSpeed(): boolean {
    return false;
  }

  /**
   * No-op for live. VOD sets `currentTime = 0` on load, but live streams
   * start at the live edge and shouldn't be seeked on initial load.
   */
  public override onVideoLoaded(_video: IContentControlVideoSource): void {
    // No need to set currentTime for live streams
  }

  /**
   * Returns the end time of the last HLS fragment (player seconds).
   * Used by mark-in logic to know the latest possible mark time.
   */
  public override getEndTimeForMarkIn(_currentVideoTimeInMs: number): number {
    return this._hlsVideoSource.playlistEndTimeSecs;
  }

  /**
   * Live uses UTC timestamps for mark-in rather than relative video time.
   */
  public override shouldUseTimestampForMarkIn(): boolean {
    return true;
  }

  public override convertPlayerTimeToTimestampMs(playerTimeSecs: number): number {
    return this._hlsVideoSource.convertPlayerRelativeTimeToUtcTimestampMs(playerTimeSecs);
  }

  /**
   * Returns directives whose end time is at or before the first fragment's
   * `programDateTime` — these have scrolled out of the HLS window and should be removed.
   *
   * @param directives         Full list of current editing directives
   * @param _selectedSegmentId Unused in live mode (selection state is not relevant here)
   * @returns The subset of directives that have expired and can be discarded
   */
  public override cleanupOldDirectives(
    directives: ContentControlledEditingDirective[],
    _selectedSegmentId?: number,
  ): ContentControlledEditingDirective[] {
    const pst = this._hlsVideoSource.firstFragment?.programDateTime ?? 0;
    return directives.filter((x) => x.endsAtMs <= pst);
  }

  /**
   * Returns the current UI time as a UTC timestamp in milliseconds,
   * computed from the HLS fragments' `programDateTime`.
   */
  public override getCurrentTimeForUIAsTimestampMs(): number {
    return this._hlsVideoSource.convertPlayerRelativeTimeToUtcTimestampMs(this.currentTimeForUISecs);
  }

  /**
   * Seeks to the last fragment's start position (live edge).
   */
  public override moveToLiveEdge(): void {
    const last = this._hlsVideoSource.lastFragment;
    if (last) this._hlsVideoSource.seekToVideoPlayerRelativeTimeInSecs(last.start);
  }

  /**
   * Creates a VideoClip segment at the current playhead position.
   * The clip spans from the current UTC timestamp for the clip's duration,
   * with two keyframes (start and end) at 100% size.
   *
   * @param clip  The video clip to insert, including its ID, name, and duration
   */
  public override onVideoClipAdd(clip: VideoClip): void {
    if (!clip.durationMs || clip.durationMs <= 0) {
      return;
    }
    const playheadMs = this._hlsVideoSource.convertPlayerRelativeTimeToUtcTimestampMs(this.currentTimeForUISecs) ?? 0;
    const segment = this._manager.createNewSegment();
    segment.mediaId = Number(clip.id);
    segment.mediaType = ContentControlledMediaTypeEnum.Video;
    segment.action = ContentControlledEventActionEnum.VideoClip;
    segment.title = clip.name ?? '';
    segment.startsAtMs = playheadMs;
    segment.endsAtMs = playheadMs + clip.durationMs;
    segment.instructions = undefined;
    segment.subtitles = undefined;
    segment.startFrame = '';
    segment.endFrame = '';
    segment.keyframes = [
      new ContentControlledKeyframe(
        playheadMs,
        0,
        0,
        100,
        100,
        false,
      ),
      new ContentControlledKeyframe(
        playheadMs + clip.durationMs,
        0,
        0,
        100,
        100,
        false,
      ),
    ];
    this.segmentToEvent(segment);
  }

  /**
   * Cleans up clip elements, event listeners, and destroys the HLS.js instance.
   */
  public override destroy(): void {
    this._videoClipVideoElement = null;
    this._activeVideoClipDirective = null;
    this._videoClipElements.forEach((el) => {
      el.pause();
      el.clearSource();
      el.load();
    });
    this._videoClipElements.clear();
    this._mainVideoListenerRemovers.forEach((remove) => remove());
    this._mainVideoListenerRemovers = [];
    this._mainVideoSyncSetup = false;

    // Remove all player event listeners
    for (const entry of this._boundEventHandlers) {
      entry.source?.removeEventListener(entry.event, entry.handler);
    }
    this._boundEventHandlers = [];

    // Destroy HLS.js instance
    this._hlsVideoSource.destroy();

    super.destroy();
  }

  // ---------------------------------------------------------------------------
  // Private: HLS manifest-ready callback
  // ---------------------------------------------------------------------------

  /**
   * Called by the HLS video source when the manifest is parsed and ready.
   * Handles UI-side initialization: muting, autoplay, initial seek, and
   * triggering the video-loaded callback if {@link setupVideo} has completed.
   */
  private async _onManifestReady(): Promise<void> {
    this._hlsVideoSource.muted = true;
    try {
      await this._hlsVideoSource.play();
    } catch (e) {
      this._log('warn', 'Auto-play failed on manifest parsed:', e);
    }
    this._hlsVideoSource.seekToVideoPlayerRelativeTimeInSecs(0);
    this._currentTimeForUISecs = this._hlsVideoSource.rawCurrentTimeSecs;

    if (this._videoSetupComplete) {
      this.onVideoLoadedEventHandler();
    }
  }

  // ---------------------------------------------------------------------------
  // Private: Event handling helpers
  // ---------------------------------------------------------------------------

  /**
   * Adds a DOM event listener to the player element and tracks it for cleanup.
   *
   * @param event    DOM event name (e.g. `'play'`, `'pause'`)
   * @param handler  Listener function to attach
   */
  private _addPlayerListener(event: string, handler: EventListener): void {
    this._hlsVideoSource.addEventListener(event, handler);
    this._boundEventHandlers.push({ source: this._hlsVideoSource, event, handler });
  }

  /**
   * Handles HLS level updates: updates live status and recalculates zoom levels
   * when the stream duration grows (e.g. after buffer config is updated on LEVEL_LOADED).
   */
  private _onLevelUpdated(): void {
    if (this._isLive) this._updateLiveStatus();

    const currentDuration = this._hlsVideoSource.maxBufferLengthSecs;
    if (currentDuration > 0 && currentDuration !== this._lastZoomDurationSecs && this._zoom && this._timelineRef) {
      this._lastZoomDurationSecs = currentDuration;
      this._zoom.calculateZoomLevels(currentDuration);
      this._timelineRef.onChangeEffectingTimeline(true);
    }
  }

  /**
   * Checks whether the player is at the live edge and updates {@link _isLive}.
   * Compares `liveSyncPosition` against `currentTimeSecs` with a threshold.
   */
  private _updateLiveStatus(): void {
    const liveSyncPos = this._hlsVideoSource.liveSyncPosition;
    if (!liveSyncPos) return;
    const currentTimeSecs = this._hlsVideoSource.activeFrag ? this._hlsVideoSource.rawCurrentTimeSecs : 0;
    const behindLiveSecs = liveSyncPos - currentTimeSecs;
    this._isLive = behindLiveSecs <= this.LIVE_THRESHOLD_SECS;
  }

  // ---------------------------------------------------------------------------
  // Private: Video clip management
  // ---------------------------------------------------------------------------

  /**
   * Sets up play/pause/seeked synchronization between the main HLS video
   * and the active clip video overlay. Called once on first clip activation.
   * Subsequent calls are no-ops (`_mainVideoSyncSetup` guard).
   */
  private _setupMainVideoSync(): void {
    if (this._mainVideoSyncSetup || !this._mainVideoElement) return;
    this._mainVideoSyncSetup = true;
    const main = this._mainVideoElement;

    const onPlay = () => {
      if (this._videoClipVideoElement && this._videoClipVideoElement.paused) {
        this._videoClipVideoElement.play();
      }
    };
    const onPause = () => {
      if (this._videoClipVideoElement && !this._videoClipVideoElement.paused) {
        this._videoClipVideoElement.pause();
      }
    };
    const onSeeked = () => {
      if (!this._videoClipVideoElement || !this._activeVideoClipDirective) return;
      const mainTimestampMs = this._hlsVideoSource.convertPlayerRelativeTimeToUtcTimestampMs(main.currentTimeSecs);
      const clipTimeSec = (mainTimestampMs - this._activeVideoClipDirective.startsAtMs) / 1000;
      const clamped = Math.max(0, Math.min(clipTimeSec, this._videoClipVideoElement.durationSecs || 0));
      if (Number.isFinite(clamped)) {
        this._videoClipVideoElement.seekToVideoPlayerRelativeTimeInSecs(clamped);
      }
    };

    main.addEventListener('play', onPlay);
    main.addEventListener('pause', onPause);
    main.addEventListener('seeked', onSeeked);
    this._mainVideoListenerRemovers.push(
      () => {
        main.removeEventListener('play', onPlay);
        main.removeEventListener('pause', onPause);
        main.removeEventListener('seeked', onSeeked);
      },
    );
  }

  /**
   * Lazily creates and caches a video element for a clip overlay.
   * Fetches the preview URL from the media repository and sets it as the source.
   *
   * @param mediaId  Media ID to create/retrieve the clip element for
   * @returns The clip video source
   */
  private _getOrCreateClipVideoElement(mediaId: number): IContentControlVideoSource {
    let source = this._videoClipElements.get(mediaId);
    if (source) return source;

    const element = document.createElement('video');
    element.crossOrigin = 'anonymous';
    element.playsInline = true;
    element.muted = true;
    source = new ContentControlVideoSource(element);
    this._videoClipElements.set(mediaId, source);

    this._mediaRepository.getMediaPreviewUrl(mediaId).then((url) => {
      if (this._videoClipElements.get(mediaId) === source) {
        element.src = url;
      }
    });

    return source;
  }

  /**
   * Seeks a clip video element to the correct offset, waiting for metadata
   * if the element isn't ready yet.
   *
   * @param clip     The clip video source to seek
   * @param timeSec  Desired offset in seconds
   */
  private _seekClipToOffset(clip: IContentControlVideoSource, timeSec: number): void {
    const apply = () => {
      const clamped = Math.max(0, Math.min(timeSec, clip.durationSecs || 0));
      if (Number.isFinite(clamped)) {
        clip.seekToVideoPlayerRelativeTimeInSecs(clamped);
      }
    };

    if (clip.readyState >= 1) {
      apply();
    } else {
      clip.addEventListener('loadedmetadata', apply, { once: true });
    }
  }

  // ---------------------------------------------------------------------------
  // Private: Logging
  // ---------------------------------------------------------------------------

  /**
   * Logs a styled message to the console when `_printEnabled` is true.
   *
   * @param level    Console log level: `'log'`, `'warn'`, or `'error'`
   * @param message  Values to include in the log output
   */
  private _log(level: 'log' | 'warn' | 'error', ...message: unknown[]): void {
    if (this._printEnabled) {
      console[level](
        '%c[LIVE-VM-V2] ',
        'background: #222; color: #bada55',
        ...message,
      );
    }
  }
}
